import { Injectable } from '@angular/core';
import { IChecklistV2ForList, IChecklistV2Viewer, ICheckpoint2AnswerModel } from '@fi-shared/checklist/checklist-v2';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { getClientGuid } from '@fi-shared/utils/client-guid';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { debounceTime, filter, map, takeUntil } from 'rxjs/operators';
import { AccessRightType, ApplicationSettingsType, ObjectTypes } from '@fi-shared/types';
import { FiSignalRHelperService, IChecklist2ForBadge } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { IChecklist2ViewerChangedEventData } from '@fi-shared/fi-signal-r/fi-signal-r';
import { bufferDebounce } from '@fi-shared/utils/buffer-debounce';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';

@Injectable()
export class FiChecklist2SyncService {
    constructor(
        private fiSignalRService: FiSignalRService,
        private fiSignalRHelperService: FiSignalRHelperService,
        private fiChecklist2Service: FiChecklist2Service,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private fiUserDataService: FiUserDataService,
    ) {
        this.subscribeToNotCompletedChecklistsEvents();
        this.startDueDateTimePassedCheck();
    }

    //# region checklists list

    subscribeOnChecklistAnswersChangeForList(args: {
        getCheckListsFn: () => IChecklistV2ForList[];
        ngOnDestroy$: Observable<void>;
        callback: (checklistToScheduleIds: number[]) => void;
    }) {
        const listChanged$ = new Subject<number>();

        listChanged$
            .pipe(
                takeUntil(args.ngOnDestroy$),
                bufferDebounce(1000),
                filter((ids) => !!ids.length),
                map((ids) => [...new Set(ids)]),
            ) // receive unique ids
            .subscribe((checklistToScheduleIds) => {
                args.callback(checklistToScheduleIds);
            });

        this.fiSignalRService.checklist2ViewerChangedEvent.subscribe((data) => {
            if (data.ClientGuid === getClientGuid()) return;

            const checklists = args.getCheckListsFn();
            const currentChecklist = checklists.find(
                (item) =>
                    item.ChecklistToScheduleId === data.ChecklistToScheduleId &&
                    item.BusinessUnitId === data.BusinessUnitId,
            );
            if (currentChecklist) {
                listChanged$.next(currentChecklist.ChecklistToScheduleId);
            }
        }, args.ngOnDestroy$);
    }

    //# endregion

    //# region checklist viewer

    subscribeOnChecklistAnswersChange(args: {
        viewer: IChecklistV2Viewer;
        ngOnDestroy$: Subject<void>;
        afterChange: (data: IChecklist2ViewerChangedEventData) => void;
    }) {
        const viewer = args.viewer;
        this.fiSignalRService.checklist2ViewerChangedEvent.subscribe((data) => {
            if (data.ClientGuid === getClientGuid()) {
                // don't do anything if current user made changes
                return;
            }

            if (
                data.ChecklistToScheduleId === viewer.ChecklistToScheduleId &&
                data.BusinessUnitId === viewer.BusinessUnitId
            ) {
                viewer.IsSetNotApplicable = data.IsNotApplicable;

                if (data.ChecklistPeriodId === viewer.PeriodId) {
                    viewer.Answers.forEach((answer, idx) => {
                        const updatedAnswer = data.UpdatedAnswers.find(
                            (updatedAnswer) => updatedAnswer.CheckpointId === answer.CheckpointId,
                        );
                        if (updatedAnswer) {
                            viewer.Answers[idx] = updatedAnswer;
                        }
                    });
                    viewer.CheckpointUnitSettings.forEach((unitSetting, idx) => {
                        const updatedUnitSetting = data.UpdatedCheckpoint2UnitSettings.find(
                            (updatedUnitSetting) => updatedUnitSetting.CheckpointId === unitSetting.CheckpointId,
                        );
                        if (updatedUnitSetting) {
                            viewer.CheckpointUnitSettings[idx] = updatedUnitSetting;
                            if (updatedUnitSetting.IsNotApplicable) {
                                // clear current answer for not applicable checkpoints
                                const notApplicableUpdatedAnswer = viewer.Answers.find(
                                    (ans) => ans.CheckpointId === updatedUnitSetting.CheckpointId,
                                );
                                if (notApplicableUpdatedAnswer) {
                                    this.resetAnswer(notApplicableUpdatedAnswer);
                                }
                            }
                        }
                    });
                }

                args.afterChange(data);
            }
        }, args.ngOnDestroy$);
    }

    resetAnswer(answer: ICheckpoint2AnswerModel) {
        answer.Id = undefined;
        answer.Value = undefined;
        if (answer.Comments.length) {
            answer.Comments[0].Content = undefined;
        }
        answer.MediaBankFileReferences = [];
    }

    //# endregion

    //# region current checklists badge

    private _checklistChanged$ = new Subject<void>();
    private _checklistAuditChanged$ = new Subject<void>();

    private _notCompletedChecklistsV2$ = new BehaviorSubject<IChecklist2ForBadge[]>([]);

    get notCompletedChecklistsV2Count$(): Observable<number> {
        return this._notCompletedChecklistsV2$.pipe(map((v) => v.length));
    }

    private _notCompletedChecklistV2Audits$ = new BehaviorSubject<IChecklist2ForBadge[]>([]);

    get notCompletedChecklistV2AuditCount$(): Observable<number> {
        return this._notCompletedChecklistV2Audits$.pipe(map((v) => v.length));
    }

    private subscribeToNotCompletedChecklistsEvents() {
        this.fiSignalRService.dbOperationEvent.subscribe((data) => {
            if (data.ObjectType === ObjectTypes.Checklist2 || data.ObjectType === ObjectTypes.ChecklistSchedule) {
                this._checklistChanged$.next();
                this._checklistAuditChanged$.next();
            }
        });
        this.fiSignalRService.checklist2ViewerChangedEvent.subscribe((data) => {
            /*
                        if (data.ClientGuid === getClientGuid())
                            return;
            */
            this.updateNotCompletedChecklists(
                data.ChecklistToScheduleId,
                data.BusinessUnitId,
                data.NotFilledCheckpointsCount,
                data.IsNotApplicable,
                data.AuditMode,
            );
        });

        // subscribe to initial data changes
        this.fiSignalRHelperService.notCompletedChecklistsV2$.subscribe((v) => {
            this._notCompletedChecklistsV2$.next(v);
        });
        this.fiSignalRHelperService.notCompletedChecklistV2Audits$.subscribe((v) => {
            this._notCompletedChecklistV2Audits$.next(v);
        });

        // load  not completed data from backend
        if (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.ChecklistV2Local,
                AccessRightType.Read,
            )
        ) {
            this._checklistChanged$.pipe(debounceTime(1000)).subscribe(() => {
                this.fiChecklist2Service.getNotCompletedChecklists(false).then((notCompletedChecklists) => {
                    this._notCompletedChecklistsV2$.next(notCompletedChecklists);
                });
            });
        }
        if (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.ChecklistV2AuditLocal,
                AccessRightType.Read,
            )
        ) {
            this._checklistAuditChanged$.pipe(debounceTime(1500)).subscribe(() => {
                this.fiChecklist2Service.getNotCompletedChecklists(true).then((notCompletedAuditChecklists) => {
                    this._notCompletedChecklistV2Audits$.next(notCompletedAuditChecklists);
                });
            });
        }
    }

    updateNotCompletedChecklists(
        checklistToScheduleId: number,
        businessUnitId: number,
        notCompletedCheckpointsCount: number,
        isChecklistNotApplicable: boolean,
        auditMode: boolean,
    ) {
        const notCompletedSubj: BehaviorSubject<IChecklist2ForBadge[]> = !auditMode
            ? this._notCompletedChecklistsV2$
            : this._notCompletedChecklistV2Audits$;
        const notCompletedChecklists = notCompletedSubj.value;
        const currItemIdx = notCompletedChecklists.findIndex(
            (item) => item.ChecklistToScheduleId === checklistToScheduleId && item.BusinessUnitId === businessUnitId,
        );
        const showInBadge = notCompletedCheckpointsCount > 0 && !isChecklistNotApplicable;
        if (currItemIdx === -1 && showInBadge) {
            // not completed checklist not in badge, request backend if it should be there
            if (!auditMode) {
                this._checklistChanged$.next();
            } else {
                this._checklistAuditChanged$.next();
            }
        } else if (currItemIdx !== -1 && !showInBadge) {
            // completed checklist in badge, just remove it
            notCompletedChecklists.splice(currItemIdx, 1);
            notCompletedSubj.next(notCompletedChecklists);
        }
    }

    //# endregion

    //# region due date time passed check
    private readonly dueDateTimeDiffMS: number = 5000;
    private startDueDateTimePassedCheck() {
        setInterval(() => {
            this.checkNotCompletedInternal(this._notCompletedChecklistsV2$.value, () => this._checklistChanged$.next());
            this.checkNotCompletedInternal(this._notCompletedChecklistV2Audits$.value, () =>
                this._checklistAuditChanged$.next(),
            );
        }, 5000);
    }

    private checkNotCompletedInternal(notCompletedChecklists: IChecklist2ForBadge[], onDueDateTimePassed: () => void) {
        const now = new Date();
        notCompletedChecklists.forEach((item) => {
            const diffMs = new Date(item.DueDateTime).getTime() - now.getTime();
            if (diffMs < -this.dueDateTimeDiffMS) {
                onDueDateTimePassed();
                this._checklistAuditChanged$.next();
            }
        });
    }
    //# endregion
}
